import React, { useState, useCallback, memo, useRef, useEffect } from "react";
import "./OptimizedImage.scss";
import NotFoundImg from "@/assets/common/not-found.webp";

// LRU 缓存类 - 限制缓存大小，防止内存泄漏
class LRUCache<K, V> {
  private cache: Map<K, V>;
  private maxSize: number;

  constructor(maxSize: number) {
    this.cache = new Map();
    this.maxSize = maxSize;
  }

  get(key: K): V | undefined {
    if (!this.cache.has(key)) {
      return undefined;
    }
    const value = this.cache.get(key)!;
    this.cache.delete(key);
    this.cache.set(key, value);
    return value;
  }

  set(key: K, value: V): void {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }
    this.cache.set(key, value);
    if (this.cache.size > this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) {
        this.cache.delete(firstKey);
      }
    }
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }
}

const imageLoadedCache = new LRUCache<string, boolean>(500);

export interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number | string;
  height?: number | string;
  placeholder?: string;
  loading?: "lazy" | "eager";
  onLoad?: () => void;
  onError?: () => void;
  /** 图片加载成功时触发，可获取原生img元素 */
  onImageLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  fallback?: string;
  quality?: "low" | "medium" | "high";
  priority?: boolean;
  onClick?: () => void;
  /** 图片填充模式，默认 cover */
  objectFit?: "cover" | "contain" | "fill" | "none";
  maxRetries?: number; // 最大重试次数
  retryDelay?: number; // 重试延迟（毫秒）
}

export const OptimizedImage: React.FC<OptimizedImageProps> = memo(
  ({
    src,
    alt,
    className = "",
    width,
    height,
    onClick,
    placeholder,
    loading = "lazy",
    onLoad,
    onError,
    onImageLoad,
    fallback = NotFoundImg,
    quality = "medium",
    priority = false,
    objectFit = "cover",
    maxRetries = 3,
    retryDelay = 1000,
  }) => {
    const qualityMap = {
      low: 0.5,
      medium: 0.75,
      high: 1,
    };

    const getOptimizedSrc = useCallback(
      (originalSrc: string) => {
        if (
          !originalSrc ||
          originalSrc.startsWith("data:") ||
          originalSrc.startsWith("blob:")
        ) {
          return originalSrc;
        }

        // 如果是外部URL，直接返回
        if (originalSrc.startsWith("http")) {
          return originalSrc;
        }

        // 如果是静态CDN资源（/static/），直接返回，不添加质量参数
        if (originalSrc.startsWith("/static/")) {
          return originalSrc;
        }

        // 添加质量参数
        const qualityValue = qualityMap[quality];
        const separator = originalSrc.includes("?") ? "&" : "?";
        return `${originalSrc}${separator}q=${qualityValue}`;
      },
      [quality]
    );

    const optimizedSrc = getOptimizedSrc(src);

    // 检查缓存，如果已经加载过，直接设置为 true
    const [imageLoaded, setImageLoaded] = useState(
      imageLoadedCache.get(optimizedSrc) || false
    );
    const [imageError, setImageError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [currentSrc, setCurrentSrc] = useState(optimizedSrc);
    const [isInView, setIsInView] = useState(priority || loading === "eager");
    const containerRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // 处理图片加载成功
    const handleLoad = useCallback(
      (e: React.SyntheticEvent<HTMLImageElement>) => {
        setImageLoaded(true);
        // 缓存加载状态
        imageLoadedCache.set(optimizedSrc, true);
        onLoad?.();
        onImageLoad?.(e);
      },
      [onLoad, onImageLoad, optimizedSrc]
    );

    // 处理图片加载失败
    const handleError = useCallback(() => {
      if (retryCount < maxRetries && !imageError) {
        retryTimeoutRef.current = setTimeout(
          () => {
            setRetryCount(prev => prev + 1);
            // 添加时间戳强制刷新
            const separator = optimizedSrc.includes("?") ? "&" : "?";
            setCurrentSrc(`${optimizedSrc}${separator}_retry=${Date.now()}`);
            setImageError(false);
          },
          retryDelay * (retryCount + 1)
        ); // 递增延迟
      } else {
        setImageError(true);
        onError?.();
      }
    }, [retryCount, maxRetries, retryDelay, optimizedSrc, imageError, onError]);

    // 当 src 变化时，检查新的图片是否已经加载过
    useEffect(() => {
      const isCached = imageLoadedCache.get(optimizedSrc);
      if (isCached) {
        setImageLoaded(true);
      } else {
        setImageLoaded(false);
      }
      setImageError(false);
      setRetryCount(0);
      setCurrentSrc(optimizedSrc);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    }, [optimizedSrc]);

    useEffect(() => {
      if (loading === "eager" || priority) {
        setIsInView(true);
        return;
      }

      let retryTimer: NodeJS.Timeout | null = null;
      let mounted = true;

      const setupObserver = () => {
        if (!mounted) return;

        if (!containerRef.current) {
          retryTimer = setTimeout(setupObserver, 50);
          return;
        }

        observerRef.current = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (entry.isIntersecting && mounted) {
                setIsInView(true);
                observerRef.current?.disconnect();
              }
            });
          },
          {
            root: null,
            rootMargin: "200px",
            threshold: 0.01,
          }
        );

        observerRef.current.observe(containerRef.current);
      };

      setupObserver();

      return () => {
        mounted = false;
        if (retryTimer) {
          clearTimeout(retryTimer);
        }
        observerRef.current?.disconnect();
      };
    }, [priority, loading, src]);

    useEffect(() => {
      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
      };
    }, []);

    // 监听图片重载事件
    useEffect(() => {
      const handleReload = () => {
        // 只重载失败的图片
        if (imageError || retryCount > 0) {
          setImageError(false);
          setRetryCount(0);
          setImageLoaded(false);
          // 添加时间戳强制刷新
          const separator = optimizedSrc.includes("?") ? "&" : "?";
          setCurrentSrc(`${optimizedSrc}${separator}_reload=${Date.now()}`);
        }
      };

      window.addEventListener("image-reload-trigger", handleReload);
      return () => {
        window.removeEventListener("image-reload-trigger", handleReload);
      };
    }, [imageError, retryCount, optimizedSrc]);

    const shouldLoad = isInView || priority || loading === "eager";

    return (
      <div
        ref={containerRef}
        className={`optimized-image ${className}`}
        style={{ width, height }}
      >
        {!shouldLoad && placeholder && (
          <div
            className="optimized-image__placeholder"
            style={{
              backgroundImage: `url(${placeholder})`,
              width: "100%",
              height: "100%",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        )}

        {shouldLoad && (
          <img
            src={imageError ? fallback : currentSrc}
            alt={alt}
            className={`optimized-image__img ${imageLoaded ? "loaded" : ""}`}
            onLoad={handleLoad}
            onError={handleError}
            loading={loading}
            decoding="async"
            onClick={onClick}
            style={{
              width: "100%",
              height: "100%",
              objectFit,
              opacity: imageLoaded ? 1 : 0,
              transition: "opacity 0.3s ease-in-out",
            }}
          />
        )}

        {!imageLoaded && shouldLoad && !placeholder && (
          <div className="optimized-image__skeleton">
            <div className="skeleton-content" />
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // 只在这些关键 props 改变时才重新渲染
    return (
      prevProps.src === nextProps.src &&
      prevProps.quality === nextProps.quality &&
      prevProps.priority === nextProps.priority &&
      prevProps.loading === nextProps.loading
    );
  }
);

OptimizedImage.displayName = "OptimizedImage";

export default OptimizedImage;
